import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CheckCircle, XCircle, AlertTriangle, Search, RefreshCw, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';

const EligibilityWorkflowPage = () => {
  const [patients, setPatients] = useState([
    { id: '1', name: 'Smith, John', mrn: 'MRN-001', dob: '05/15/1945', primaryPayer: 'Medicare', memberId: '1EG4-TE5-MK72', eligibilityStatus: 'Active', lastChecked: '01/26/2026', coInsurance: 'None' },
    { id: '2', name: 'Johnson, Mary', mrn: 'MRN-002', dob: '08/22/1952', primaryPayer: 'UnitedHealthcare', memberId: 'UHC-789456', eligibilityStatus: 'Active', lastChecked: '01/25/2026', coInsurance: '20%' },
    { id: '3', name: 'Williams, Robert', mrn: 'MRN-003', dob: '03/10/1948', primaryPayer: 'Medicare', memberId: '3JL9-TG1-OP28', eligibilityStatus: 'Inactive', lastChecked: '01/24/2026', coInsurance: 'None' },
    { id: '4', name: 'Brown, Patricia', mrn: 'MRN-004', dob: '11/30/1955', primaryPayer: 'Aetna', memberId: 'AET-123789', eligibilityStatus: 'Active', lastChecked: '01/23/2026', coInsurance: '15%' },
    { id: '5', name: 'Davis, Michael', mrn: 'MRN-005', dob: '07/04/1960', primaryPayer: 'BCBS', memberId: 'BCBS-456123', eligibilityStatus: 'Pending', lastChecked: '01/22/2026', coInsurance: '10%' },
    { id: '6', name: 'Garcia, Jennifer', mrn: 'MRN-006', dob: '12/18/1950', primaryPayer: 'Humana', memberId: 'HUM-789012', eligibilityStatus: 'Active', lastChecked: '01/21/2026', coInsurance: 'None' },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const handleCheckEligibility = (patient) => {
    setIsChecking(true);
    toast.info(`Checking eligibility for ${patient.name}...`);
    setTimeout(() => {
      setPatients(prev => prev.map(p => 
        p.id === patient.id ? { ...p, lastChecked: new Date().toLocaleDateString(), eligibilityStatus: 'Active' } : p
      ));
      setIsChecking(false);
      toast.success(`Eligibility verified for ${patient.name}`);
    }, 1500);
  };

  const handleBatchCheck = () => {
    setIsChecking(true);
    toast.info('Running batch eligibility check...');
    setTimeout(() => {
      setPatients(prev => prev.map(p => ({ ...p, lastChecked: new Date().toLocaleDateString() })));
      setIsChecking(false);
      toast.success('Batch eligibility check complete');
    }, 3000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Active</span>;
      case 'Inactive':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Inactive</span>;
      case 'Pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Pending</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.mrn.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.memberId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: patients.length,
    active: patients.filter(p => p.eligibilityStatus === 'Active').length,
    inactive: patients.filter(p => p.eligibilityStatus === 'Inactive').length,
    pending: patients.filter(p => p.eligibilityStatus === 'Pending').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="eligibility-workflow-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-800 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">All Payer Eligibility Workflow</h1>
              <p className="text-gray-600">Verify patient insurance eligibility across all payers</p>
            </div>
          </div>
          <Button 
            onClick={handleBatchCheck} 
            disabled={isChecking}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {isChecking ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Batch Check All
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users className="w-5 h-5 text-gray-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Patients</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            <p className="text-sm text-gray-500">Active Coverage</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.inactive}</p>
            <p className="text-sm text-gray-500">Inactive</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-gray-500">Pending Verification</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, MRN, or Member ID..." className="pl-10" />
          </div>
        </div>

        {/* Patients Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">MRN</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">DOB</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Primary Payer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Member ID</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Co-Insurance</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Checked</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{patient.name}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{patient.mrn}</td>
                    <td className="px-4 py-3 text-gray-600">{patient.dob}</td>
                    <td className="px-4 py-3 text-gray-900">{patient.primaryPayer}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{patient.memberId}</td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(patient.eligibilityStatus)}</td>
                    <td className="px-4 py-3 text-gray-600">{patient.coInsurance}</td>
                    <td className="px-4 py-3 text-gray-500">{patient.lastChecked}</td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleCheckEligibility(patient)}
                        disabled={isChecking}
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${isChecking ? 'animate-spin' : ''}`} /> Verify
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EligibilityWorkflowPage;
